import moment from 'moment';
import { CHARGING_STATES } from '../Constants';

export const getStatusClass = (status) => {
  let cssClass = '';
  switch (status) {
    case CHARGING_STATES.AVAILABLE:
      cssClass = 'status-available';
      break;
    case CHARGING_STATES.PREPARING:
      cssClass = 'status-preparing';
      break;
    case CHARGING_STATES.FINISHING:
      cssClass = 'status-finishing';
      break;
    case CHARGING_STATES.DISCONNECTED:
      cssClass = 'status-disconnecting';
      break;
    case CHARGING_STATES.CHARGING:
      cssClass = 'status-charging';
      break;
    case CHARGING_STATES.SUSPENDED_EV:
      cssClass = 'status-suspended';
      break;
    case CHARGING_STATES.READY:
      cssClass = 'status-ready';
      break;
    default:
      cssClass = '';
  }
  return cssClass;
};

export const humanizeTotalTimeElapsed = (ms) => {
  if (!ms) {
    return '';
  }
  let minutes = Math.floor((ms / 1000 / 60) % 60);
  if (typeof minutes !== 'number') {
    minutes = 0;
  }
  const hours = Math.floor((ms / 1000 / 3600) % 24);
  const humanMinutes = minutes.toString().padStart(2, '0');
  const humanHours = hours.toString().padStart(2, '0');

  return hours ? `${humanHours}h : ${humanMinutes}m` : `${humanMinutes}m`;
};

export const tryParseJSONObject = (jsonString) => {
  try {
    const o = JSON.parse(jsonString);
    if (o && typeof o === 'object') {
      return o;
    }
  } catch (e) {
    console.error('error on tryParseJSONObject', e);
  }

  return false;
};

export const pad = (num) => {
  return ('0' + num).slice(-2);
};

export const getDatesForDateRange = (date) => {
  return moment(date).unix() * 1000;
};

const parseDataSafely = (data) => {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch (error) {
      console.error('Error parsing data:', error);
      return null;
    }
  }
  return data;
};

export const returnUpdatedChargerList = (list, data) => {
  if (!list?.length) return list;
  const dataToJson = parseDataSafely(data);
  if (!dataToJson) return list;

  const prop = dataToJson.slug ? 'slug' : 'id';
  const value = dataToJson.slug || dataToJson.chargePointId;

  if (!value) return list;

  const chargerIndex = list.findIndex((ch) => ch[prop] === value);
  if (chargerIndex === -1) return list;

  const charger = list[chargerIndex];
  const { chargePointConnectors } = charger;

  if (!chargePointConnectors?.length) return list;

  const connectorIndex = chargePointConnectors.findIndex(
    (conn) => conn.id === dataToJson.chargePointConnectorId
  );

  if (connectorIndex === -1) return list;

  const chargers = [...list];
  const updatedCharger = { ...charger };
  const connectors = [...chargePointConnectors];

  connectors[connectorIndex] = returnUpdatedConnector(
    {
      ...connectors[connectorIndex],
      ...(dataToJson.isLoading !== undefined && { isButtonLoading: dataToJson.isLoading })
    },
    dataToJson
  );
  updatedCharger.chargePointConnectors = connectors;
  chargers[chargerIndex] = updatedCharger;

  return chargers;
};

export const returnUpdatedConnector = (connector, data) => {
  const updatedConnector = { ...connector };

  if (data.status) {
    updatedConnector.status = data.status;
  }

  if (data.status && data.status !== CHARGING_STATES.FINISHING) {
    // Map WebSocket data to session fields
    // WebSocket sends: totalEnergyWh, powerActiveImportEnergy, totalTimeElapsed
    updatedConnector.totalTimeElapsed = data.totalTimeElapsed || 0;
    updatedConnector.status = data.status;
    // Store both field names for backwards compatibility
    updatedConnector.totalEnergyWh = data?.totalEnergyWh || 0;
    updatedConnector.totalEnergyConsumed = data?.totalEnergyWh || 0;
    updatedConnector.energy_consumption = data.energy_consumption;
    updatedConnector.label = data.label || '';
    updatedConnector.transactionId = data.transactionId || null;
    updatedConnector.powerActiveImportEnergy = data.powerActiveImportEnergy || null;
    updatedConnector.powerActiveImportValue = data.powerActiveImportEnergy || null;
    updatedConnector.userId = data.userId || null;
    updatedConnector.refidTagId = data.rfid_tag_id || null;
  }
  if (data.isLoading) {
    updatedConnector.isButtonLoading = true;
  } else {
    updatedConnector.isButtonLoading = false;
  }

  return updatedConnector;
};

export const calculateTotalEnergyConsumption = (chargers) => {
  if (!chargers || !chargers.length) return '0.00';

  let totalEnergy = 0;

  chargers.forEach((charger) => {
    if (charger.sessions && charger.sessions.length) {
      charger.sessions.forEach((session) => {
        // Use session.totalEnergyConsumed directly (V2 now stores energy on session like V1)
        const sessionEnergy = (session.totalEnergyConsumed ?? 0) / 1000;
        totalEnergy += Math.max(0, Number(sessionEnergy));
      });
    }
  });

  return totalEnergy.toFixed(2);
};

/**
 * Calculate total price for a list of chargers
 * @param {Array} chargePoints - List of charge points with sessions
 * @returns {number} - Total price
 */
export const calculateTotalPrice = (chargePoints) => {
  return (
    chargePoints.reduce((total, chargePoint) => {
      if (!chargePoint.sessions?.length) {
        return total;
      }

      const sessionsCost = chargePoint.sessions.reduce((sessionTotal, session) => {
        if (session.paymentIntent) {
          const amount = session.paymentIntent?.amount || 0;
          return sessionTotal + parseFloat(amount);
        }

        // Use session.totalEnergyConsumed directly (V2 now stores energy on session like V1)
        // Still use chargePoint.prices for the rate
        if (!chargePoint.prices?.length) {
          return sessionTotal;
        }

        const sortedPrices = [...chargePoint.prices].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        const currentPrice = parseFloat(sortedPrices[0]?.price) || 0;

        // Get energy from session.totalEnergyConsumed (Wh)
        const sessionEnergy = session.totalEnergyConsumed ?? 0;
        const sessionCost = Math.max(0, sessionEnergy) * currentPrice;

        return sessionTotal + sessionCost;
      }, 0);
      return total + sessionsCost;
    }, 0) / 100
  );
};

/**
 * Count total sessions for a list of chargers
 * @param {Array} chargePoints - List of charge points with sessions
 * @returns {number} - Total number of sessions
 */
export const calculateTotalSessions = (chargePoints) => {
  return chargePoints.reduce((total, chargePoint) => {
    return total + (chargePoint.sessions ? chargePoint.sessions.length : 0);
  }, 0);
};

/**
 * Calculate total energy for sessions
 * @param {Array} sessions - List of sessions
 * @returns {string} - Total energy consumption formatted with 2 decimal places
 */
export const calculateTotalEnergySum = (sessions) => {
  if (!sessions || !Array.isArray(sessions)) return 0;

  return sessions
    .reduce((total, session) => {
      // Use session.totalEnergyConsumed directly (V2 now stores energy on session like V1)
      const sessionEnergy = (session.totalEnergyConsumed ?? 0) / 1000;
      return total + Math.max(0, sessionEnergy);
    }, 0)
    .toFixed(2);
};
